// Pest Control Common-Fix Protocols — the 15 service calls that make up
// ~80% of a Northeast pest shop's week. Each entry is a deterministic
// IPM checklist: target pest → observed evidence → recommended products
// (from the curated PESTICIDES catalog) → application protocol → follow-up
// cadence. Tapping a protocol in the app writes a pre-filled pesticide-log
// draft (product, EPA #, target pest, application type) so the tech only
// has to add quantity + address. Zero LLM calls.
//
// Sources: UC IPM, Penn State Extension, NPMA field guides (2024).

export const PEST_COMMON_FIXES = [
  {
    id: "german_roach_apartment_light",
    label: "German cockroach · apartment · light",
    target_pest: "cockroaches",
    signs: "1-5 roaches seen at night, no active harborage found yet",
    protocol: [
      "Advion Cockroach Gel Bait (EPA 100-1484) — pea-sized dabs in every void within 3 ft of sighting",
      "Follow-up in 14 days · re-treat only fresh activity",
    ],
    recommended_product: "Advion Cockroach Gel Bait",
    application_type: "crack_crevice",
    follow_up_days: 14,
    notes: "No sprays around bait — repels roaches. Warn tenant to not clean baited voids for 30 days.",
  },
  {
    id: "german_roach_food_service",
    label: "German cockroach · restaurant · heavy",
    target_pest: "cockroaches",
    signs: "Fecal spotting on shelves, live roaches during day inspection, egg cases in warm equipment",
    protocol: [
      "Rotate bait actives every quarter to prevent resistance",
      "Advion (Indoxacarb) → Maxforce FC Magnum (Fipronil) → Maxforce Gel (Hydramethylnon)",
      "Insect growth regulator: Nyguard IGR (EPA 1021-1603) applied to non-food harborage",
      "Weekly follow-up first month, then monthly",
    ],
    recommended_product: "Maxforce FC Magnum Roach Bait",
    application_type: "crack_crevice",
    follow_up_days: 7,
    notes: "Health inspector wants a written bait rotation log — Pesticide Log auto-generates.",
  },
  {
    id: "american_roach_basement",
    label: "American / oriental roach · basement",
    target_pest: "cockroaches",
    signs: "Large roaches in basement / sewer clean-outs, moisture-driven",
    protocol: [
      "Advance 375A Granular Bait around perimeter, sewer clean-outs, and sump areas",
      "Correct moisture: seal grade slope, fix leaking traps",
      "Quarterly service",
    ],
    recommended_product: "Advance 375A Select Granular Bait",
    application_type: "perimeter",
    follow_up_days: 30,
    notes: "Fix the moisture and 80% of the problem is gone — bait is the finisher.",
  },
  {
    id: "odorous_ants",
    label: "Odorous house ants (sugar ants)",
    target_pest: "ants",
    signs: "Small brown ants trailing kitchen counters, sweet-food-seeking",
    protocol: [
      "Terro PCO Liquid Ant Bait (EPA 149-8) directly on trails — bait stations at entry points",
      "Do NOT spray the trail; ants must carry bait back to satellite colonies",
      "Re-check in 10 days · replenish bait as consumed",
    ],
    recommended_product: "Terro PCO Liquid Ant Bait",
    application_type: "spot",
    follow_up_days: 10,
    notes: "Odorous house ants have satellite colonies — killing foragers doesn't fix it. Use bait, be patient.",
  },
  {
    id: "carpenter_ants",
    label: "Carpenter ants · structural",
    target_pest: "ants",
    signs: "Large black ants, wood shavings (frass), nocturnal activity",
    protocol: [
      "Locate the primary nest — Termidor SC (EPA 7969-210) applied to gallery + entry points",
      "Advion Ant Gel Bait (EPA 100-1498) in wall voids where nest is suspected",
      "Repair moisture-damaged wood",
      "Follow-up 30 days",
    ],
    recommended_product: "Termidor SC Termiticide/Insecticide",
    application_type: "injection",
    follow_up_days: 30,
    notes: "Carpenter ants tunnel in wet wood — always find and fix the leak.",
  },
  {
    id: "pavement_ants",
    label: "Pavement ants · perimeter",
    target_pest: "ants",
    signs: "Small ants nesting under sidewalks, driveways, foundation slab",
    protocol: [
      "Talstar Professional perimeter band 3 ft up × 3 ft out",
      "Advance 375A granular on ant hills in cracks",
    ],
    recommended_product: "Talstar Professional",
    application_type: "perimeter",
    follow_up_days: 30,
  },
  {
    id: "bed_bugs_residential",
    label: "Bed bugs · residential unit",
    target_pest: "bedbugs",
    signs: "Bites in linear pattern, blood spots on sheets, cast skins in mattress seams",
    protocol: [
      "Temprid FX (EPA 432-1543) — mattress seams, box spring, wall/floor junction, headboard",
      "Steam treatment on mattress + baseboard (heat kills eggs)",
      "Encasements on mattress + box spring for 12 months",
      "Follow-up in 10-14 days · minimum 2 treatments",
    ],
    recommended_product: "Temprid FX",
    application_type: "spot",
    follow_up_days: 14,
    notes: "One treatment is NEVER enough. Book the 2-week follow-up on the same ticket.",
  },
  {
    id: "bed_bugs_multi_unit",
    label: "Bed bugs · multi-unit apartment",
    target_pest: "bedbugs",
    signs: "Multiple units reporting bites — building-wide protocol",
    protocol: [
      "Inspect target unit + all adjacent (above/below/left/right)",
      "Crossfire Bed Bug Concentrate (EPA 1021-2596) on treated units",
      "Bedlam Plus (EPA 1021-2574) on non-porous surfaces",
      "Building-wide reinspection in 21 days",
    ],
    recommended_product: "Crossfire Bed Bug Concentrate",
    application_type: "spot",
    follow_up_days: 21,
  },
  {
    id: "norway_rats_exterior",
    label: "Norway rats · exterior bait stations",
    target_pest: "rodents",
    signs: "Burrows near foundation, greasy runways along fence lines, droppings",
    protocol: [
      "T1 tamper-resistant stations every 50 ft along active runways",
      "Contrac Blox (EPA 12455-79) — 4 blocks per station, top up weekly",
      "Log every station: bait taken vs untouched, weather notes",
    ],
    recommended_product: "Contrac Blox",
    application_type: "bait_station",
    follow_up_days: 7,
    notes: "Bait must be in tamper-resistant stations outdoors — EPA rule. Photograph each station location.",
  },
  {
    id: "roof_rats_interior",
    label: "Roof rats · attic / interior",
    target_pest: "rodents",
    signs: "Scratching in ceiling at night, droppings in attic, grease marks along rafters",
    protocol: [
      "Snap traps FIRST inside structure (no bait indoors — dying rats stink up walls)",
      "Terad3 Blox (EPA 12455-131) in exterior stations only",
      "Seal all penetrations >0.5 inch — inspect roof line, soffits, dryer vents",
    ],
    recommended_product: "Terad3 Blox",
    application_type: "bait_station",
    follow_up_days: 7,
    notes: "Cholecalciferol (Terad3) is single-feed — safer near dogs than anticoag.",
  },
  {
    id: "house_mice_interior",
    label: "House mice · commercial kitchen / office",
    target_pest: "rodents",
    signs: "Droppings behind appliances, gnawed food packaging, urine tracks under UV",
    protocol: [
      "Ditrac All-Weather Blox (EPA 12455-72) in tamper-resistant stations along walls",
      "Snap traps in ceiling voids where visible bait is a health-code issue",
      "Exclusion: steel wool + expanding foam on all pipe/wire penetrations",
    ],
    recommended_product: "Ditrac All-Weather Blox",
    application_type: "bait_station",
    follow_up_days: 14,
  },
  {
    id: "subterranean_termite_bait",
    label: "Subterranean termites · monitoring / bait",
    target_pest: "termites",
    signs: "Mud tubes on foundation, damaged wood sounds hollow, swarmers in spring",
    protocol: [
      "Sentricon Always Active (EPA 62719-608) — stations every 10 ft around structure",
      "Monitor quarterly · hit any active station with Recruit HD",
      "Annual bond inspection with photo report",
    ],
    recommended_product: "Sentricon Always Active",
    application_type: "bait_station",
    follow_up_days: 90,
    notes: "Termite work usually carries a $$$ bond — Pesticide Log auto-generates the annual proof.",
  },
  {
    id: "yellowjacket_ground_nest",
    label: "Yellowjackets · ground nest",
    target_pest: "wasps",
    signs: "Yellow-and-black wasps entering hole in ground / wall void",
    protocol: [
      "Treat at dusk when all foragers are back in the nest",
      "Wasp-Freeze II (EPA 499-515) shot into entrance — 8-15 ft jet-stream",
      "Dust nest opening with Delta Dust · plug hole 24 hrs later",
    ],
    recommended_product: "Wasp-Freeze II",
    application_type: "spot",
    follow_up_days: 7,
    notes: "Don't stand in the flight path. If nest is in a wall void, dust > liquid.",
  },
  {
    id: "spiders_perimeter",
    label: "Spiders · foundation web / general",
    target_pest: "spiders",
    signs: "Webs on eaves, foundation corners, window frames",
    protocol: [
      "Talstar Professional (EPA 279-3206) perimeter band 3 ft up × 3 ft out",
      "Physically knock down existing webs (spiders don't return to broken webs)",
      "Quarterly service — spider populations rebuild with prey availability",
    ],
    recommended_product: "Talstar Professional",
    application_type: "perimeter",
    follow_up_days: 90,
  },
  {
    id: "fleas_pet_reintroduction",
    label: "Fleas · post-treatment or new home",
    target_pest: "fleas",
    signs: "Bites on ankles, flea dirt in carpets, pet scratching",
    protocol: [
      "Vacuum thoroughly — fleas emerge from pupae on vibration",
      "Precor 2000 Plus Premise Spray (EPA 2724-489) — carpets, upholstery, pet bedding",
      "Nyguard IGR (EPA 1021-1603) to break the flea life cycle",
      "Treat pet with vet-provided oral (not our job, but tell the customer)",
      "Re-treat in 14 days · flea eggs hatch on staggered schedule",
    ],
    recommended_product: "Precor 2000 Plus Premise Spray",
    application_type: "broadcast",
    follow_up_days: 14,
    notes: "IGR is the secret weapon — kills eggs before they mature.",
  },
];

// Return the top-N protocols for a given target pest (used by the quick-picker
// on the pesticide-log form so techs see the roach protocols when the target
// pest select is 'German Cockroach', 'Roach', 'cockroaches', etc.).
export function protocolsForPest(target_pest, limit = 6) {
  if (!target_pest) return PEST_COMMON_FIXES.slice(0, limit);
  const key = String(target_pest).toLowerCase();
  // Loose category match — 'German Cockroach' → 'cockroaches', 'Ant' → 'ants'
  const CATEGORY_HINTS = {
    cockroach: "cockroaches", roach: "cockroaches",
    ant: "ants",
    rat: "rodents", mice: "rodents", mouse: "rodents", rodent: "rodents",
    termite: "termites",
    "bed bug": "bedbugs", bedbug: "bedbugs",
    wasp: "wasps", hornet: "wasps", yellowjacket: "wasps", bee: "wasps",
    spider: "spiders",
    flea: "fleas",
    mosquito: "mosquitoes",
  };
  let category = null;
  for (const [hint, cat] of Object.entries(CATEGORY_HINTS)) {
    if (key.includes(hint)) { category = cat; break; }
  }
  const filtered = category
    ? PEST_COMMON_FIXES.filter((f) => f.target_pest === category)
    : PEST_COMMON_FIXES;
  return (filtered.length ? filtered : PEST_COMMON_FIXES).slice(0, limit);
}
